/**
 * API Cache Configuration
 * Defines caching strategies for different API endpoints
 */

import type { AxiosRequestConfig, AxiosResponse } from 'axios'
import { cacheService } from './cacheService'

interface CacheConfig {
  ttl: number
  storage: 'memory' | 'local' | 'session'
  invalidateOn?: string[]
  keyGenerator?: (config: AxiosRequestConfig) => string
}

// Cache configurations for different endpoints
const cacheConfigs: Record<string, CacheConfig> = {
  // Accounts - cache for 10 minutes in memory
  'GET /accounts': {
    ttl: 10 * 60 * 1000,
    storage: 'memory',
    invalidateOn: ['POST /accounts', 'PUT /accounts', 'DELETE /accounts']
  },

  // Account details - cache for 5 minutes
  'GET /accounts/:id': {
    ttl: 5 * 60 * 1000,
    storage: 'memory',
    invalidateOn: ['PUT /accounts/:id', 'DELETE /accounts/:id']
  },

  // Clients - cache for 15 minutes in local storage
  'GET /clients': {
    ttl: 15 * 60 * 1000,
    storage: 'local',
    invalidateOn: ['POST /clients', 'PUT /clients', 'DELETE /clients']
  },

  // Vendors - cache for 15 minutes
  'GET /vendors': {
    ttl: 15 * 60 * 1000,
    storage: 'local',
    invalidateOn: ['POST /vendors', 'PUT /vendors', 'DELETE /vendors']
  },

  // Reports - cache for 30 minutes
  'GET /reports/income-statement': {
    ttl: 30 * 60 * 1000,
    storage: 'session',
    keyGenerator: (config) => {
      const params = config.params || {}
      return `income-statement-${params.startDate}-${params.endDate}`
    }
  },

  'GET /reports/balance-sheet': {
    ttl: 30 * 60 * 1000,
    storage: 'session',
    keyGenerator: (config) => {
      const params = config.params || {}
      return `balance-sheet-${params.date}`
    }
  },

  'GET /reports/trial-balance': {
    ttl: 30 * 60 * 1000,
    storage: 'session',
    keyGenerator: (config) => {
      const params = config.params || {}
      return `trial-balance-${params.startDate}-${params.endDate}`
    }
  },

  // Dashboard data - cache for 2 minutes
  'GET /dashboard': {
    ttl: 2 * 60 * 1000,
    storage: 'memory'
  },

  // User profiles - cache for 10 minutes
  'GET /users/:id': {
    ttl: 10 * 60 * 1000,
    storage: 'memory',
    invalidateOn: ['PUT /users/:id']
  },

  // Roles and permissions - cache for 1 hour
  'GET /roles': {
    ttl: 60 * 60 * 1000,
    storage: 'local'
  },

  'GET /permissions': {
    ttl: 60 * 60 * 1000,
    storage: 'local'
  }
}

// Track invalidation patterns
const invalidationPatterns: Map<string, RegExp[]> = new Map()

/**
 * Initialize cache invalidation patterns
 */
function initializeInvalidationPatterns() {
  Object.entries(cacheConfigs).forEach(([endpoint, config]) => {
    if (config.invalidateOn) {
      config.invalidateOn.forEach(invalidator => {
        if (!invalidationPatterns.has(invalidator)) {
          invalidationPatterns.set(invalidator, [])
        }

        // Convert endpoint pattern to regex
        const pattern = endpoint
          .replace(/:[^/]+/g, '[^/]+')
          .replace(/\*/g, '.*')

        invalidationPatterns.get(invalidator)!.push(new RegExp(`^${pattern}$`))
      })
    }
  })
}

/**
 * Get cache configuration for an endpoint
 */
function getCacheConfig(method: string, url: string): CacheConfig | null {
  const endpoint = `${method ? method.toUpperCase() : 'GET'} ${url}`

  // Try exact match first
  if (cacheConfigs[endpoint]) {
    return cacheConfigs[endpoint]
  }

  // Try pattern matching
  for (const [pattern, config] of Object.entries(cacheConfigs)) {
    const regex = new RegExp(
      `^${pattern.replace(/:[^/]+/g, '[^/]+').replace(/\*/g, '.*')}$`
    )

    if (regex.test(endpoint)) {
      return config
    }
  }

  return null
}

/**
 * Generate cache key for a request
 */
function generateCacheKey(config: AxiosRequestConfig, cacheConfig?: CacheConfig): string {
  if (cacheConfig?.keyGenerator) {
    return cacheConfig.keyGenerator(config)
  }

  const method = config.method ? config.method.toUpperCase() : 'GET'
  const url = config.url || ''
  const params = config.params ? JSON.stringify(config.params) : ''
  const data = config.data ? JSON.stringify(config.data) : ''

  return `${method}-${url}-${params}-${data}`
}

/**
 * Check if a request is cacheable
 */
function isCacheable(config: AxiosRequestConfig): boolean {
  // Only cache GET requests by default
  const method = config.method ? config.method.toUpperCase() : 'GET'
  if (method !== 'GET') {
    return false
  }

  // Skip if explicitly disabled
  if (config.headers?.['X-No-Cache'] === 'true') {
    return false
  }

  return true
}

/**
 * Invalidate cache entries based on a request
 */
function invalidateCache(config: AxiosRequestConfig) {
  const method = config.method ? config.method.toUpperCase() : ''
  const url = config.url || ''
  const endpoint = `${method} ${url}`

  // Get patterns to invalidate
  const patterns = invalidationPatterns.get(endpoint) || []

  patterns.forEach(pattern => {
    cacheService.invalidatePattern(pattern)
  })
}

/**
 * Cache request interceptor
 */
export function cacheRequestInterceptor(config: AxiosRequestConfig): AxiosRequestConfig {
  // Skip if not cacheable
  if (!isCacheable(config)) {
    return config
  }

  const cacheConfig = getCacheConfig(config.method || 'GET', config.url || '')
  if (!cacheConfig) {
    return config
  }

  const cacheKey = generateCacheKey(config, cacheConfig)
  const cached = cacheService.get<AxiosResponse>(cacheKey, {
    storage: cacheConfig.storage
  })

  if (cached) {
    // Return cached response by creating a Promise that resolves to the cached data
    // We need to create a synthetic successful response that bypasses the request
    const cacheError: any = new Error('Cache hit')
    cacheError.isCache = true
    cacheError.response = cached
    cacheError.config = config

    // Store the cache hit for the request interceptor to handle
    ;(config as any).__cacheHit = cacheError
    return config
  }

  // Store cache config for response interceptor
  ;(config as any).cacheConfig = { key: cacheKey, config: cacheConfig }

  return config
}

/**
 * Cache response interceptor
 */
export function cacheResponseInterceptor(response: AxiosResponse): AxiosResponse {
  const config = response.config as any

  // Cache the response if configured
  if (config.cacheConfig) {
    const { key, config: cacheConfig } = config.cacheConfig

    cacheService.set(key, response, {
      ttl: cacheConfig.ttl,
      storage: cacheConfig.storage,
      etag: response.headers['etag']
    })
  }

  // Invalidate related caches for mutation operations
  const method = config.method ? config.method.toUpperCase() : ''
  if (['POST', 'PUT', 'PATCH', 'DELETE'].includes(method)) {
    invalidateCache(config)
  }

  return response
}

/**
 * Cache error interceptor
 */
export function cacheErrorInterceptor(error: any): Promise<any> {
  // Invalidate cache on errors for mutations
  if (error.config) {
    const method = error.config.method ? error.config.method.toUpperCase() : ''
    if (['POST', 'PUT', 'PATCH', 'DELETE'].includes(method)) {
      invalidateCache(error.config)
    }
  }

  return Promise.reject(error)
}

/**
 * Configure API caching
 */
export function configureApiCache(customConfigs?: Record<string, CacheConfig>) {
  // Merge custom configurations
  if (customConfigs) {
    Object.assign(cacheConfigs, customConfigs)
  }

  // Initialize invalidation patterns
  initializeInvalidationPatterns()
}

/**
 * Clear API cache
 */
export function clearApiCache(patterns?: string[]) {
  if (patterns) {
    patterns.forEach(pattern => {
      cacheService.invalidatePattern(new RegExp(pattern))
    })
  } else {
    cacheService.clear()
  }
}

/**
 * Get API cache statistics
 */
export function getApiCacheStats() {
  return cacheService.getStats()
}

// Initialize on module load
initializeInvalidationPatterns()
